// Mirror of Shared/Calculators/ConcreteCalculator.cs.
import type { CalcDef, ResultLine } from "../types.js";
import * as F from "../format.js";

const fields: CalcDef["fields"] = [
  { key: "areaLengthFt", kind: "number", default: 10, min: 0.5, max: 200 },
  { key: "areaWidthFt", kind: "number", default: 10, min: 0.5, max: 200 },
  { key: "thicknessIn", kind: "number", default: 4, min: 1, max: 48 },
  {
    key: "bagSize",
    kind: "select",
    defaultChoice: "80lb",
    choices: [
      { value: "80lb", set: { bagYieldCuFt: 0.6 } },
      { value: "60lb", set: { bagYieldCuFt: 0.45 } },
      { value: "50lb", set: { bagYieldCuFt: 0.375 } },
    ],
  },
  { key: "wastePct", kind: "number", default: 5, min: 0, max: 25 },
  { key: "pricePerBag", kind: "number", default: 5, min: 0, max: 50 },
];

function compute(i: Record<string, number>): Record<string, number> {
  const l = F.get(i, "areaLengthFt");
  const w = F.get(i, "areaWidthFt");
  const thick = F.get(i, "thicknessIn");
  const yield_ = F.get(i, "bagYieldCuFt");
  const waste = F.get(i, "wastePct");
  const price = F.get(i, "pricePerBag");

  const volCuFt = l * w * (thick / 12);
  const volWithWaste = volCuFt * (1 + waste / 100);
  const cuYards = volWithWaste / 27;
  const bags = yield_ > 0 ? F.ceilUnits(volWithWaste / yield_) : 0;
  const cost = bags * price;

  return {
    volCuFt: F.round(volCuFt, 2),
    volWithWaste: F.round(volWithWaste, 2),
    cuYards: F.round(cuYards, 2),
    bags,
    cost: F.round(cost, 2),
  };
}

function present(_i: Record<string, number>, r: Record<string, number>): ResultLine[] {
  const bags = F.get(r, "bags");
  return [
    { label: "Volume", value: `${F.dec(F.get(r, "volCuFt"))} cu ft` },
    { label: "Cubic yards", value: `${F.dec(F.get(r, "cuYards"))} cu yd` },
    { label: "Bags to buy", value: `${F.intStr(bags)} ${F.plural(bags, "bag", "bags")}`, highlight: true },
    { label: "Est. materials cost", value: F.usd(F.get(r, "cost")), sub: "rough — bagged concrete only, excludes rebar, forms & labor" },
  ];
}

export const concrete: CalcDef = { slug: "concrete-calculator", fields, compute, present };
